import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/data/site";

export default function ContactPage() {
  const rows = [
    {
      icon: Phone,
      label: "Call us",
      value: SITE.phoneDisplay,
      href: `tel:${SITE.phoneTel}`,
      testid: "contact-phone-link",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      value: "Chat with a CA — replies in minutes",
      href: SITE.whatsapp,
      testid: "contact-whatsapp-link",
      external: true,
    },
    {
      icon: Mail,
      label: "Email",
      value: SITE.email,
      href: `mailto:${SITE.email}`,
      testid: "contact-email-link",
    },
    {
      icon: MapPin,
      label: "Office",
      value: SITE.address,
      href: "https://www.google.com/maps/search/?api=1&query=Angel+Mega+Mall+Kaushambi+Ghaziabad",
      testid: "contact-map-link",
      external: true,
    },
  ];

  return (
    <>
      <Seo
        title="Contact SAIVU — Book a free 20-minute CA consultation"
        description="Call, WhatsApp or write to SAIVU (Saivu Consulting Pvt Ltd). Office: Angel Mega Mall, Kaushambi, Ghaziabad. Free 20-minute consultation — no sales pitch."
        path="/contact"
      />

      <section className="bg-white pt-28 pb-24 sm:pt-32" data-testid="contact-hero">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-gold-deep sm:text-sm">Contact</p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.15] tracking-tight text-navy-900 sm:text-5xl">
                Tell us what you're building.
              </h1>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ink sm:text-lg">
                The first conversation is free — twenty minutes with a qualified chartered
                accountant, no sales pitch.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="mt-9">
              <div className="flex flex-col gap-6">
                {rows.map((r) => (
                  <a
                    key={r.label}
                    href={r.href}
                    target={r.external ? "_blank" : undefined}
                    rel={r.external ? "noopener noreferrer" : undefined}
                    className="group flex items-start gap-4"
                    data-testid={r.testid}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-line bg-mist transition-colors group-hover:border-navy-900">
                      <r.icon className="h-5 w-5 text-navy-800" />
                    </span>
                    <span>
                      <span className="block font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">
                        {r.label}
                      </span>
                      <span className="mt-1 block text-[15px] font-medium leading-relaxed text-navy-900">
                        {r.value}
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="rounded-lg border border-line bg-mist p-7 sm:p-9" data-testid="contact-form-card">
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gold-deep">Free callback</p>
                <h2 className="mt-3 text-2xl font-semibold text-navy-900">Book your free consultation.</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-ink">
                  Fill this in and a CA calls you back — usually within a few working hours.
                </p>
                <div className="mt-7 rounded-lg border border-line bg-white p-6 sm:p-7">
                  <LeadForm id="contact-lead" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
